// The Monero balance, which only this device can compute.
//
// Every other row in the wallet arrives from the API. Monero's cannot: the
// chain hides amounts and has no address index, so the balance is whatever
// falls out of scanning blocks with the view key — and the view key stays
// here. The API sends the row with `deviceOnly` set and a zero amount, and
// this hook replaces that zero with the real number.
//
// Two things follow, and both are visible to the user:
//
//   • It is late. A scan takes seconds to minutes depending on how long the
//     app was closed, so the last known balance is kept and shown immediately,
//     with the scan updating it. Showing 0 until the scan finishes would be a
//     different and false statement — "you have nothing" rather than "not
//     counted yet".
//   • It needs the seed. For a wallet without a PIN that resolves silently and
//     the scan runs on its own. With a PIN it does not, and the user is not
//     prompted for one just to refresh a balance: the last known figure stays
//     until something they do already has the seed in hand.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSeed } from '../lib/wallet';
import { useAuthReady } from './useAuthReady';
import type { MoneroSyncProgress } from '../lib/monero';

/** Last known balance per address, so a cold open shows a number rather than
 *  zero. A cache of a public fact about the user's own wallet: the amount, not
 *  a key, and only on their own device. */
const LAST_KNOWN_PREFIX = 'xmr:balance:';

export const moneroBalanceKey = (address: string) => ['xmr-balance', address] as const;

function readLastKnown(address: string): bigint | null {
  try {
    const raw = localStorage.getItem(LAST_KNOWN_PREFIX + address);
    return raw ? BigInt(raw) : null;
  } catch {
    return null;
  }
}

function writeLastKnown(address: string, value: bigint): void {
  try {
    localStorage.setItem(LAST_KNOWN_PREFIX + address, value.toString());
  } catch {
    /* a balance we cannot cache just means a zero on the next cold open */
  }
}

export interface MoneroBalanceState {
  /** Piconero. Null when nothing has ever been scanned on this device. */
  balance: bigint | null;
  /** True while a scan is running. */
  scanning: boolean;
  /** 0–100 during a scan. */
  percent: number;
  /** Why the last scan did not happen or did not finish, if it did not. */
  error: 'seed_unavailable' | 'scan_failed' | null;
  /** Run a scan now. Safe to call repeatedly; one runs at a time. */
  scan: () => Promise<void>;
}

/**
 * Scan for and report the Monero balance of one address.
 *
 * `address` and `createdAt` come from the wallet's own address row —
 * `createdAt` decides where scanning starts, and getting it wrong in the
 * "later" direction would skip deposits.
 */
export function useMoneroBalance(
  address: string | null,
  createdAt?: string | null,
  options?: { auto?: boolean },
): MoneroBalanceState {
  const { canFetch: ready, initData } = useAuthReady();
  const qc = useQueryClient();
  const [balance, setBalance] = useState<bigint | null>(null);
  const [scanning, setScanning] = useState(false);
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState<MoneroBalanceState['error']>(null);
  // One scan at a time: the wallet holds a lock on its own cache, and two
  // would fight over it.
  const running = useRef(false);

  useEffect(() => {
    setBalance(address ? readLastKnown(address) : null);
  }, [address]);

  const scan = useCallback(async () => {
    if (!address || !ready || running.current) return;
    running.current = true;
    setScanning(true);
    setError(null);
    try {
      const mnemonic = await getSeed(initData);
      if (!mnemonic) {
        // A PIN wallet with no reauth in hand. Not a failure worth shouting
        // about — the last known balance stays on screen.
        setError('seed_unavailable');
        return;
      }
      const { openMoneroWallet } = await import('../lib/monero');
      const wallet = await openMoneroWallet({ mnemonic, initData, createdAt });
      try {
        await wallet.sync((p: MoneroSyncProgress) => setPercent(p.percent));
        const next = await wallet.balance();
        setBalance(next);
        writeLastKnown(address, next);
        // Published so useWallet can fold it into the balance list, and every
        // total downstream is computed from one number.
        qc.setQueryData(moneroBalanceKey(address), next.toString());
      } finally {
        await wallet.close();
      }
    } catch (e) {
      console.error('[xmr] scan failed:', e);
      setError('scan_failed');
    } finally {
      running.current = false;
      setScanning(false);
    }
  }, [address, createdAt, initData, qc, ready]);

  const auto = options?.auto ?? true;
  useEffect(() => {
    if (auto && address && ready) void scan();
    // Deliberately not re-running on every `scan` identity change: this is a
    // minutes-long job, not something to restart when a parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, address, ready]);

  return { balance, scanning, percent, error, scan };
}
